import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import api from "api";
import "./board.scss";
import Ticket from "components/ticket";
import Columns from "components/board/columns";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "redux/reducers";
import { BoardMemberType, BoardType, ContactType, RootState, SubscriptionType } from "types";
import Background from "components/board/background";
import Report from "components/board/report";
import Loader from "components/loader";
import TicketReport from "components/board/ticketReport";
import { MyBoardContext } from "context/boardContext";
import { toast } from "react-toastify";
import CurrentTicketReport from "components/board/currentTicketReport/currentTicketReport";

function Board() {
    const params: any = useParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.user);

    const [board, setBoard] = useState<BoardType>({} as BoardType);
    const [preference, setPreference] = useState(localStorage.getItem("sortPreference"));
    const [sortOption, setSortOption] = useState<number>(0);
    const [boardMenuOpen, setBoardMenuOpen] = useState(false);
    const [boardMembersMenuOpen, setBoardMembersMenuOpen] = useState(false);
    const [boardTicketsMenuOpen, setBoardTicketsMenuOpen] = useState(false);
    const [boardCurrentTicketsMenuOpen, setBoardCurrentTicketsMenuOpen] = useState(false);
    const [backgroundOpen, setBackgroundOpen] = useState(false);
    const [background, setBackground] = useState("");
    const [reportOpen, setReportOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [boardDescription, setBoardDescription] = useState<string | undefined>(board?.Board?.Description);
    const [clientContacts, setClientContacts] = useState<ContactType[]>([]);
    const [viewArchived, setViewArchived] = useState<boolean>(false);

    const isAdmin = useMemo(() => {
        if (!board.Members) {
            return false;
        } else {
            for (var x of board.Members) {
                if (user && x.Email === user.Email) {
                    if (x.RoleId === 2) {
                        return true;
                    }
                }
            }
            return false;
        }
    }, [board.Members, user]);

    const deleteBoard = () => {
        let deleteBoard = window.confirm(`Are you sure you want to delete this board?`);

        if (deleteBoard) {
            api.board.delete(params.boardId).then(() => {
                history.replace(`/client/${params.clientId}/dashboard`);
            });
        }
    };

    const addBoardMember = (id: number) => {
        api.board.member.add(params.boardId, id, 1).then(() => {
            getBoard();
        });
    };

    const deleteBoardMember = (id: number) => {
        dispatch(
            actions.board.set({
                ...board,
                Members: [...board.Members].filter((x: any) => x.Id !== id),
            })
        );
        api.board.member.delete(id).then((data) => {
            getBoard();
        });
    };

    const updateBoard = () => {
        if (boardDescription) {
            api.board.update(params.boardId, boardDescription, board!.Board.Background || "");
        }
    };

    const deleteTicket = (id: number) => {
        let deleteTicket = window.confirm(`Are you sure you want to delete this ticket?`);
        if (deleteTicket) {
            api.ticket.delete(id, user.Id).then(() => {
                history.goBack();
                getBoard();
            });
        }
    };

    const refresh = () => {
        getBoard();
    };

    const getBoard = useCallback(() => {
        api.board
            .get(params.boardId)
            .then((data: BoardType) => {
                data.Members = data.Members.sort((a, b) => (a.Name < b.Name ? -1 : 1));
                dispatch(actions.board.set(data));
                setBoard(data);
            })
            .finally(() => setLoading(false));
    }, [dispatch, params.boardId]);

    const getClientContacts = useCallback(() => {
        api.clientContact.getAll(params.clientId).then((data: ContactType[]) => {
            let resSorted = data.sort((a, b) => (a.Name < b.Name ? -1 : 1));
            dispatch(actions.clientContacts.set(resSorted));
            setClientContacts(resSorted);
        });
    }, [params.clientId, dispatch]);

    const getBoards = useCallback(() => {
        api.board.getAll(params.clientId).then((data) => {
            dispatch(actions.boards.set(data));
        });
    }, [params.clientId, dispatch]);

    const updateMemberRole = (id: number, roleId: number, index: number) => {
        api.board.member.update(id, roleId);
        const members: any = [...board.Members];
        const [removed] = members.splice(index, 1);
        members.splice(index, 0, { ...removed, RoleId: roleId });
        dispatch(
            actions.board.set({
                ...board,
                Members: members,
            })
        );
    };

    const sortCards = (option: number) => {
        setSortOption(option);
        localStorage.setItem("sortPreference", `${option}`);
    };

    useEffect(() => {
        getBoard();
        getClientContacts();
        getBoards();
    }, [getBoard, getBoards, getClientContacts, setPreference]);

    useEffect(() => {
        setBackground(board?.Board?.Background!);
        setBoardDescription(board?.Board?.Description);
    }, [board]);

    return (
        <>
            <div
                id="board"
                style={{
                    background: `${
                        background
                            ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0)), url(https://workflow-api.amasocial.co.za/api/board/${params.boardId}/background/${background})`
                            : //</>? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0)), url(https://workflow-api.${MainDomain}.co.za/api/board/${params.boardId}/background/${background})`
                              `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0))`
                    }`,
                }}
                onClick={() => {
                    setBoardMenuOpen(false);
                    setBoardMembersMenuOpen(false);
                    setBackgroundOpen(false);
                    setReportOpen(false);
                    setBoardTicketsMenuOpen(false);
                    setBoardCurrentTicketsMenuOpen(false);
                }}
            >
                <div id="board-header">
                    <Link to={`/client/${params.clientId}/dashboard`} title={"Back to Dashboard"}>
                        <i className="fas fa-home"></i>
                    </Link>
                    <Link to={`/client/${params.clientId}/calendar?boardId=${params.boardId}`} title={"View Board Calendar"}>
                        <i className="fas fa-calendar"></i>
                    </Link>
                    {viewArchived && <h2>Archived Tickets</h2>}
                    <input
                        type="text"
                        value={boardDescription !== undefined ? boardDescription : ""}
                        onChange={(e) => {
                            setBoardDescription(e.currentTarget.value);
                        }}
                        onBlur={updateBoard}
                    />
                    {/* <Link to={`/client/${params.clientId}/list/${params.boardId}`}>
            <i className="fal fa-list"></i>
          </Link>
          <i className="fal fa-border-all"></i> */}
                    <i
                        className="far fa-ellipsis-h"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!boardMenuOpen && boardMembersMenuOpen) {
                                setTimeout(() => {
                                    setBoardMenuOpen(!boardMenuOpen);
                                }, 200);
                            } else {
                                setBoardMenuOpen(!boardMenuOpen);
                            }
                            setBoardMembersMenuOpen(false);
                        }}
                    ></i>
                    <ul id="board-menu" className={boardMenuOpen ? "open" : ""}>
                        <li
                            onClick={(e) => {
                                e.stopPropagation();
                                setBoardMembersMenuOpen(true);
                                setBoardMenuOpen(false);
                            }}
                        >
                            Members
                        </li>
                        <li
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewArchived(!viewArchived);
                                setBoardMenuOpen(false);
                            }}
                        >
                            {viewArchived ? "View Tickets" : "View Archived Tickets"}
                        </li>
                        <li
                            onClick={(e) => {
                                e.stopPropagation();
                                setBoardMenuOpen(false);
                                setTimeout(() => {
                                    setBackgroundOpen(true);
                                }, 200);
                            }}
                        >
                            Background
                        </li>
                        <hr></hr>
                        {isAdmin === true && (
                            <li
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setBoardMenuOpen(false);
                                    setBoardCurrentTicketsMenuOpen(true);
                                }}
                            >
                                Current Ticket Report
                            </li>
                        )}

                        <li
                            onClick={(e) => {
                                e.stopPropagation();
                                setBoardMenuOpen(false);
                                setBoardTicketsMenuOpen(true);
                            }}
                        >
                            Audit Report
                        </li>
                        <hr></hr>
                        <li onClick={deleteBoard}>Delete Board</li>
                        <hr></hr>
                        <li onClick={() => sortCards(0)}>Default Sort</li>
                        <li onClick={() => sortCards(1)}>Sort Tickets By Due Date</li>
                        <li onClick={() => sortCards(2)}>Sort Tickets By Name (Alphabetically)</li>
                        <li onClick={() => sortCards(3)}>Sort Tickets By Assignee (Alphabetically)</li>
                    </ul>
                    <div id="board-members-menu" className={boardMembersMenuOpen ? "open" : ""} onClick={(e) => e.stopPropagation()}>
                        <p>Board Members</p>
                        <ul>
                            {board?.Members?.map((x: any, i: number) => {
                                return (
                                    <li key={i}>
                                        {x.RoleId === 2 && <i className="fas fa-crown" title="Admin" onClick={() => updateMemberRole(x.Id, 1, i)}></i>}
                                        {x.Name} {x.Surname} {x.RoleId === 1 && <i className="fal fa-crown admin" title="Make Admin" onClick={() => updateMemberRole(x.Id, 2, i)}></i>}
                                        <i className="fal fa-times" onClick={() => deleteBoardMember(x.Id)}></i>
                                    </li>
                                );
                            })}
                        </ul>
                        <select value={0} onChange={(e) => addBoardMember(parseInt(e.currentTarget.value))}>
                            <option value={0}>Add</option>
                            {clientContacts
                                .filter((x: ContactType) => !board?.Members?.map((y: BoardMemberType) => y.ContactId).includes(x.Id))
                                .map((x: any, i: number) => (
                                    <option value={x.Id} key={i}>
                                        {x.Name} {x.Surname}
                                    </option>
                                ))}
                        </select>
                    </div>
                    {/* <div id="board-tickets-menu" className={boardMembersMenuOpen ? "open" : ""} onClick={(e) => e.stopPropagation()}>
                        <p>Board Members</p>
                        <select value={0} onChange={(e) => addBoardMember(parseInt(e.currentTarget.value))}>
                            <option value={0}>Add</option>
                            {clientContacts
                                .filter((x: any) => !board?.Members?.map((y: any) => y.Id).includes(x.Id))
                                .map((x: any, i: number) => (
                                    <option value={x.Id} key={i}>
                                        {x.Name} {x.Surname}
                                    </option>
                                ))}
                        </select>
                    </div> */}
                </div>

                <MyBoardContext.Provider value={{ isArchived: viewArchived }}>
                    <Columns sortOption={sortOption} setSortOption={setSortOption} viewArchived={viewArchived} />
                    <Ticket deleteTicket={deleteTicket} refresh={refresh} />
                    <Report open={reportOpen} setOpen={setReportOpen} />
                    <TicketReport open={boardTicketsMenuOpen} setOpen={setBoardTicketsMenuOpen} />
                    <CurrentTicketReport open={boardCurrentTicketsMenuOpen} setOpen={setBoardCurrentTicketsMenuOpen} />
                    <Background open={backgroundOpen} setOpen={setBackgroundOpen} />
                </MyBoardContext.Provider>
            </div>
            <Loader show={loading} />
        </>
    );
}

export default Board;
